// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { TimeSeriesData } from '../../lib/models';

interface PaybackPeriodProps {
  data: TimeSeriesData[];
  title?: string;
}

/**
 * Payback Period visualization showing cumulative net cost over time
 * Annotates the year when the vaccination programme "pays for itself"
 */
export const PaybackPeriod: React.FC<PaybackPeriodProps> = ({
  data,
  title = 'Payback period',
}) => {

  const chartData = useMemo(() => {
    if (!data.length) {
      return { years: [], cumulativeMillions: [], paybackIndex: -1 };
    }

    // Ensure year 0 is included with cumulative net cost = 0
    const hasYear0 = data.some((point) => {
      const year = typeof point.year === 'string' ? parseInt(point.year) : point.year;
      return year === 0;
    });

    let processedData = [...data];
    if (!hasYear0) {
      // Add year 0 with cumulative net cost = 0
      processedData = [
        { ...data[0], year: 0, cumulativeNetCost: 0 },
        ...data,
      ];
    }
    
    // Sort by year to ensure proper ordering
    processedData.sort((a, b) => {
      const yearA = typeof a.year === 'string' ? parseInt(a.year) : a.year;
      const yearB = typeof b.year === 'string' ? parseInt(b.year) : b.year;
      return yearA - yearB;
    });

    const years = processedData.map((point) => {
      const year = typeof point.year === 'string' ? parseInt(point.year) : point.year;
      return year;
    });
    const cumulative = processedData.map((point) => point.cumulativeNetCost ?? 0);
    const cumulativeMillions = cumulative.map((value) => value / 1_000_000);
    // Find payback period: first year after year 0 where cumulative net cost <= 0
    // Skip index 0 (year 0) since it's always 0 by definition
    const paybackIndex = cumulative.findIndex((value, index) => index > 0 && value <= 0);

    return { years, cumulativeMillions, paybackIndex };
  }, [data]);

  const annotations: Partial<Plotly.Annotations>[] =
    chartData.paybackIndex >= 0
      ? [
          {
            x: chartData.years[chartData.paybackIndex],
            y: chartData.cumulativeMillions[chartData.paybackIndex],
            text: `Pays back in ${chartData.years[chartData.paybackIndex]}`,
            arrowhead: 2,
            ay: -40,
            font: { color: '#34d399', size: 12 }, // Standardized savings color
            bgcolor: 'rgba(15,23,42,0.85)',
            bordercolor: '#34d399', // Standardized savings color
          },
        ]
      : [];

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
      </div>
      <div className="vaccine-demo__chart" style={{ position: 'relative' }}>
        <Plot
          data={[
            {
              x: chartData.years,
              y: chartData.cumulativeMillions,
              type: 'scatter',
              mode: 'lines+markers',
              name: 'Cumulative net cost',
              line: { color: '#5EDFCB', width: 3 }, // Standardized turquoise matching Cost Composition
              marker: { size: 8, color: '#5EDFCB' },
            },
          ]}
          layout={{
            autosize: true,
            height: 400,
            margin: { l: 90, r: 20, t: 50, b: 60 },
            xaxis: {
              title: { 
                text: 'Year',
                font: { color: '#e2e8f0', size: 13, family: 'Inter, sans-serif' },
              },
              tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
              range: chartData.years.length > 0 ? [0, Math.max(...chartData.years)] : undefined,
              tickmode: 'linear',
              tick0: 0,
              dtick: 1,
              gridcolor: '#334155',
              showgrid: true,
              gridwidth: 1,
              griddash: 'dot',
              ticklen: 8,
              tickwidth: 1,
            },
            yaxis: (() => {
              // Calculate y-axis range to ensure 0 is always visible
              const yValues = chartData.cumulativeMillions;
              if (yValues.length === 0) {
                return {
                  title: { 
                    text: 'Cumulative net cost (€M)',
                    font: { color: '#e2e8f0', size: 13, family: 'Inter, sans-serif' },
                  },
                  tickformat: ',.0f',
                  tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
                  zeroline: true,
                  zerolinecolor: '#94a3b8',
                  zerolinewidth: 2,
                  gridcolor: '#334155',
                  showgrid: true,
                  gridwidth: 1,
                  griddash: 'dot',
                };
              }

              const yMin = Math.min(...yValues);
              const yMax = Math.max(...yValues);
              const yRange = yMax - yMin;
              
              // Calculate appropriate tick spacing
              const calculateTickSpacing = (range: number): number => {
                if (range <= 0) return 1;
                const rough = Math.ceil(range / 6);
                const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
                const candidates = [1, 2, 5, 10].map((c) => c * magnitude);
                return candidates.find((c) => rough <= c) ?? candidates[candidates.length - 1];
              };
              
              const dtick = calculateTickSpacing(yRange);
              
              // Ensure 0 is always included in the y-axis range
              let yAxisMin = Math.floor(yMin / dtick) * dtick;
              let yAxisMax = Math.ceil(yMax / dtick) * dtick;
              
              if (yAxisMin > 0) {
                // All values are positive, extend range down to include 0
                yAxisMin = Math.floor(0 / dtick) * dtick - dtick;
              } else if (yAxisMax < 0) {
                // All values are negative, extend range up to include 0
                yAxisMax = Math.ceil(0 / dtick) * dtick + dtick;
              } else {
                // Range spans zero, ensure zero is included with padding
                yAxisMin = Math.min(Math.floor(yMin / dtick) * dtick - dtick, -dtick);
                yAxisMax = Math.max(Math.ceil(yMax / dtick) * dtick + dtick, dtick);
              }

              // Determine tick format based on dtick value
              // If dtick is >= 1, use no decimals; if < 1, use 1 decimal
              const tickFormat = dtick >= 1 ? ',.0f' : ',.1f';

              return {
                title: { 
                  text: 'Cumulative net cost (€M)',
                  font: { color: '#e2e8f0', size: 13, family: 'Inter, sans-serif' },
                },
                tickformat: tickFormat,
                tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
                range: [yAxisMin, yAxisMax],
                dtick: dtick,
                tick0: 0,
                tickmode: 'linear',
                zeroline: true,
                zerolinecolor: '#94a3b8',
                zerolinewidth: 2,
                gridcolor: '#334155',
                showgrid: true,
                gridwidth: 1,
                griddash: 'dot',
                ticklen: 8,
                tickwidth: 1,
                ticklabelposition: 'outside',
              };
            })(),
            annotations,
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            font: { color: '#e2e8f0' },
            hovermode: 'x unified',
          }}
          config={{ responsive: true, displaylogo: false, displayModeBar: true }}
          style={{ width: '100%', height: '400px' }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

export default PaybackPeriod;
